import { Schedule } from "@iventis/domain-model/model/schedule";
import { ScheduleConfig } from "@iventis/domain-model/model/scheduleConfig";
import { TimeSpan } from "@iventis/types";
import { isValidUuid, useConstant } from "@iventis/utilities";
import { useEffect, useMemo } from "react";
import { useLocation } from "react-router";
import { BehaviorSubject } from "rxjs";
import { useHistory } from "@iventis/subscriptions";
import { FilterType } from "./filter-rules";
import { includeThirdLevelVenue, isFilteredByDay } from "./schedule-helper-functions";
import { ScheduleSearchParams } from "./schedule-search-params";
import { ScheduleMachineContext, UpdateViewBy } from "./schedule.machine.types";

/** Enum of the available query parameters for the schedule app url */
export enum ScheduleQueryParam {
    ViewBy = "viewBy",
    CalendarStart = "start",
    CalendarEnd = "end",
    VenueLocation = "venue",
    Table = "table",
    DataField = "dataField",
    CalendarLevel = "calendarLevel",
    Day = "day",
}

export type ScheduleUrlData = { query: ScheduleSearchParams; pathname: string };

/** Union of tuples of key value pairs for the schedule query params */
export type ScheduleQueryParamPairs =
    | [ScheduleQueryParam.ViewBy, FilterType]
    | [ScheduleQueryParam.CalendarStart, TimeSpan]
    | [ScheduleQueryParam.CalendarEnd, TimeSpan]
    | [ScheduleQueryParam.VenueLocation, string]
    | [ScheduleQueryParam.DataField, string]
    | [ScheduleQueryParam.CalendarLevel, string]
    | [ScheduleQueryParam.Day, string]
    | [ScheduleQueryParam.Table, "true" | "false"]; // satisfies [ScheduleQueryParam, string] // introduce when we upgrade to typescript v5

/** Hook which returns the history object and a query params subject - to be used to subscribe or write to the url query parameters or path */
export const useLocationDataWithSubject = () => {
    const history = useHistory();
    const { search, pathname } = useLocation();

    const query = useMemo(() => new ScheduleSearchParams(search), [search]);

    const query$ = useConstant(
        () => new BehaviorSubject<ScheduleUrlData>({ query, pathname })
    );

    useEffect(() => {
        query$.next({ query, pathname });
    }, [query, pathname, search]);

    return [history, query$] as const;
};

/** Gets the schedule id from the url pathname */
export const getScheduleIdFromUrlPathname = (pathname: string) => {
    const scheduleIdFromUrl = pathname.split("schedule")[1]?.replace("/", "");
    if (isValidUuid(scheduleIdFromUrl)) {
        return scheduleIdFromUrl;
    }
    return null;
};

/** Updates the URL directly with a new pathname based on the given schedule id */
export const updateUrlPathnameWithNewScheduleId = (history: ReturnType<typeof useHistory>, scheduleId: string) => {
    history.push({ ...history.location, pathname: `${history.location.pathname.split("schedule")[0]}schedule/${scheduleId ?? ""}` });
};

/** Updates the url directly, with a new query string formed from the given query */
export const updateUrlSearchParam = (history: ReturnType<typeof useHistory>, query: ScheduleSearchParams) => {
    history.push({ ...history.location, search: query.toString() });
};

export const getScheduleCalendarLevels = (scheduleConfig: ScheduleConfig) => {
    const levels: number[] = [];
    scheduleConfig.eventBlockTypes.forEach((eventBlockType) => {
        if (typeof eventBlockType.calendarLevel === "number") {
            levels.push(eventBlockType.calendarLevel);
        }
    });
    return levels;
};

/** Using the machine context and UPDATE_VIEW_BY event, updates the URL with appropriate data and removes any unwanted state from the URL */
export const updateViewByUrlFromMachine = (
    context: ScheduleMachineContext,
    event: UpdateViewBy,
    locationData$: BehaviorSubject<ScheduleUrlData>,
    history: ReturnType<typeof useHistory>
) => {
    const { query } = locationData$.getValue();
    /*
        MOVING AWAY FROM
    */
    if (context.selectedViewByOption.filterType === FilterType.Summary) {
        // If we are transitioning away from Summary view, make sure the calendar level isn't 0
        // (since calendar level 0 is only valid in summary view - translates to `showSessions = false`, see schedule.tsx `showSessions` variable)
        if (query.get(ScheduleQueryParam.CalendarLevel) === "0") {
            query.set(ScheduleQueryParam.CalendarLevel, "1");
        }
    }

    /*
        MOVING TO
    */
    if (event.payload.filterType === FilterType.VenueRunSheet) {
        // Do not allow the "regular" table view in the DCAS since it is already a tabular view
        query.set(ScheduleQueryParam.Table, "false");
        query.delete(ScheduleQueryParam.CalendarLevel);
    }

    if (event.payload.filterType === FilterType.Summary) {
        query.set(ScheduleQueryParam.CalendarLevel, "1");
    }

    if (event.payload.filterType === FilterType.VenueRunSheet || event.payload.filterType === FilterType.Summary) {
        // Wipe the calendar start/end from the search params when entering non calendar views
        query.delete(ScheduleQueryParam.CalendarStart);
        query.delete(ScheduleQueryParam.CalendarEnd);
    }

    if (event.payload.filterType === FilterType.Venue || event.payload.filterType === FilterType.VenueRunSheet) {
        if (!query.has(ScheduleQueryParam.VenueLocation)) {
            // If our view by filters on a single venue, set the venue location to the first one, if not already there
            const venues =
                context.selectedVenueLocations.length === 1
                    ? context.selectedVenueLocations
                    : [includeThirdLevelVenue(context?.schedule?.config?.venueLocationTypes) ? context.venueLocations[0].children[0] : context.venueLocations[0]];
            query.set(ScheduleQueryParam.VenueLocation, venues[0].item.id);
        }
    } else {
        // Else, delete it, since we don't keep multiple venues in URL state
        query.delete(ScheduleQueryParam.VenueLocation);
    }

    if (event.payload.filterType !== FilterType.DataField) {
        // Since we don't keep multiple data fields in URL state, delete the data field param if not viewing by single data field
        query.delete(ScheduleQueryParam.DataField);
    }
    if (isFilteredByDay(event.payload.filterType)) {
        const day = query.get(ScheduleQueryParam.Day);
        if (day == null) {
            query.set(ScheduleQueryParam.Day, context.day.toString());
        }
    } else {
        // If we're not viewing by day, make sure the "day" query param is not present
        query.delete(ScheduleQueryParam.Day);
    }

    query.set(ScheduleQueryParam.ViewBy, event.payload.filterType);
    updateUrlSearchParam(history, query);
};

export const getAndValidateInitialDay = (filterType: FilterType, availableDays: Map<number, Date>, query: ScheduleSearchParams) => {
    if (query.has(ScheduleQueryParam.Day)) {
        if (isFilteredByDay(filterType)) {
            const dayFromUrl = Number(query.get(ScheduleQueryParam.Day));
            if (availableDays.has(dayFromUrl)) {
                return dayFromUrl;
            }
        }
        query.delete(ScheduleQueryParam.Day);
    }
    // Try get day 1, but if no day 1 exists just get the first available day
    return availableDays.has(1) ? 1 : Array.from(availableDays.keys())[0];
};

export const getAndValidateInitialCalendarLevel = (filterType: FilterType, schedule: Schedule, query: ScheduleSearchParams) => {
    const calendarLevelFromUrl = query.get(ScheduleQueryParam.CalendarLevel);

    let calendarLevel: number | null;

    switch (filterType) {
        case FilterType.VenueRunSheet: {
            if (calendarLevelFromUrl) {
                query.delete(ScheduleQueryParam.CalendarLevel);
            }
            calendarLevel = null;
            break;
        }
        case FilterType.Summary: {
            if (!calendarLevelFromUrl || calendarLevelFromUrl !== "1") {
                query.set(ScheduleQueryParam.CalendarLevel, "1");
            }
            calendarLevel = 1;
            break;
        }
        default: {
            if (calendarLevelFromUrl) {
                if (getScheduleCalendarLevels(schedule.config).includes(Number(calendarLevelFromUrl))) {
                    calendarLevel = Number(calendarLevelFromUrl);
                    break;
                } else {
                    query.delete(ScheduleQueryParam.CalendarLevel);
                }
            }
            calendarLevel = 1;
            break;
        }
    }

    return calendarLevel;
};
